import { useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Card,
    CardContent,
    CircularProgress,
    Divider,
    IconButton,
    Link,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { ApiRounded, CodeRounded, ContentCopyRounded } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { copyClipboard } from '@/utils/copy-clipboard';
import { enqueueSnackbar } from 'notistack';
import { generateJavaScriptExample, CodeExampleProfile } from './code-examples';
import { API_DOCS_URL } from '@/constants';

interface StepApiCredentialsProps {
    loading: boolean;
    appId: string;
    apiKey: string | null;
    isGeneratedKey: boolean;
    profiles?: CodeExampleProfile[];
}

export const StepApiCredentials = ({
    loading,
    appId,
    apiKey,
    isGeneratedKey,
    profiles = [],
}: StepApiCredentialsProps) => {
    const { t } = useTranslation();
    const [copiedAppId, setCopiedAppId] = useState(false);
    const [copiedApiKey, setCopiedApiKey] = useState(false);
    const [copiedExample, setCopiedExample] = useState(false);

    const handleCopyAppId = () => {
        copyClipboard(
            appId,
            () => {
                enqueueSnackbar(t('str.action.copied'));
                setCopiedAppId(true);
                setTimeout(() => setCopiedAppId(false), 2000);
            },
            () => {
                enqueueSnackbar(t('error.something-wrong'));
            },
        );
    };

    const handleCopyApiKey = () => {
        if (!apiKey) return;
        copyClipboard(
            apiKey,
            () => {
                enqueueSnackbar(t('str.action.copied'));
                setCopiedApiKey(true);
                setTimeout(() => setCopiedApiKey(false), 2000);
            },
            () => {
                enqueueSnackbar(t('error.something-wrong'));
            },
        );
    };

    const handleCopyExample = (text: string) => {
        copyClipboard(
            text,
            () => {
                enqueueSnackbar(t('str.action.copied'));
                setCopiedExample(true);
                setTimeout(() => setCopiedExample(false), 2000);
            },
            () => {
                enqueueSnackbar(t('error.something-wrong'));
            },
        );
    };

    const codeExample = useMemo(
        () => generateJavaScriptExample({ appId, apiKey: apiKey || '', profiles }),
        [appId, apiKey, profiles],
    );

    return (
        <Stack spacing={3}>
            <Stack alignItems="center" py={2} px={2}>
                <Stack alignItems="center" textAlign="center">
                    <Stack
                        sx={{
                            aspectRatio: 1,
                            p: 1.5,
                            borderRadius: '50%',
                            color: 'success.main',
                            bgcolor: 'success.light',
                            justifyContent: 'center',
                            alignItems: 'center',
                            mb: 1,
                        }}
                    >
                        <ApiRounded />
                    </Stack>
                    <Typography variant="subtitle1" gutterBottom>
                        {t('onboarding.dev.api-key-title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        {t('onboarding.dev.api-key-description')}
                    </Typography>
                </Stack>
            </Stack>

            <Card variant="outlined">
                <CardContent>
                    <Stack spacing={2}>
                        {loading ? (
                            <Stack alignItems="center" py={3} spacing={2}>
                                <CircularProgress />
                                <Typography variant="body2" color="text.secondary">
                                    {t('onboarding.dev.generating-key')}
                                </Typography>
                            </Stack>
                        ) : apiKey ? (
                            <>
                                <TextField
                                    value={appId}
                                    fullWidth
                                    label={t('settings.workspace.api.app-id.label')}
                                    slotProps={{
                                        input: {
                                            readOnly: true,
                                            sx: {
                                                fontFamily: 'monospace',
                                                fontSize: '0.875rem',
                                            },
                                            endAdornment: (
                                                <Tooltip title={t('str.action.copy')}>
                                                    <IconButton
                                                        onClick={handleCopyAppId}
                                                        size="small"
                                                        color={copiedAppId ? 'info' : 'default'}
                                                    >
                                                        <ContentCopyRounded
                                                            fontSize="small"
                                                            color={copiedAppId ? 'info' : 'inherit'}
                                                        />
                                                    </IconButton>
                                                </Tooltip>
                                            ),
                                        },
                                    }}
                                />
                                <TextField
                                    value={apiKey}
                                    fullWidth
                                    multiline
                                    maxRows={3}
                                    label={t('settings.workspace.api.key.primary.label')}
                                    slotProps={{
                                        input: {
                                            readOnly: true,
                                            sx: {
                                                fontFamily: 'monospace',
                                                fontSize: '0.875rem',
                                            },
                                            endAdornment: isGeneratedKey ? (
                                                <Tooltip title={t('str.action.copy')}>
                                                    <IconButton
                                                        onClick={handleCopyApiKey}
                                                        size="small"
                                                        color={copiedApiKey ? 'info' : 'default'}
                                                    >
                                                        <ContentCopyRounded
                                                            fontSize="small"
                                                            color={copiedApiKey ? 'info' : 'inherit'}
                                                        />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : null,
                                        },
                                    }}
                                />
                                {isGeneratedKey && (
                                    <Alert severity="warning" sx={{ mt: 2 }}>
                                        <Typography variant="body2">
                                            {t('onboarding.dev.save-key-warning')}
                                        </Typography>
                                    </Alert>
                                )}
                            </>
                        ) : null}
                    </Stack>
                </CardContent>
            </Card>

            {apiKey && isGeneratedKey && (
                <>
                    <Divider sx={{ my: 2 }} />
                    <Stack spacing={2}>
                        <Typography variant="subtitle2">{t('onboarding.dev.try-it-now')}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t('onboarding.dev.example-description')}
                        </Typography>
                        <Card variant="outlined">
                            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                                <Box sx={{ position: 'relative' }}>
                                    <Box
                                        component="pre"
                                        sx={{
                                            m: 0,
                                            p: 2,
                                            bgcolor: 'grey.900',
                                            color: 'grey.100',
                                            overflow: 'auto',
                                            fontSize: '0.75rem',
                                            fontFamily: 'monospace',
                                            borderRadius: 0,
                                        }}
                                    >
                                        {codeExample.split('\n').map((line, i) => (
                                            <Box key={i} component="span" sx={{ display: 'flex', gap: 2 }}>
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        minWidth: '2ch',
                                                        textAlign: 'right',
                                                        color: 'grey.600',
                                                        userSelect: 'none',
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    {i + 1}
                                                </Box>
                                                <Box component="span">{line || ' '}</Box>
                                            </Box>
                                        ))}
                                    </Box>
                                    <Tooltip
                                        title={copiedExample ? t('str.action.copied') : t('str.action.copy')}
                                    >
                                        <IconButton
                                            onClick={() => handleCopyExample(codeExample)}
                                            size="small"
                                            color={copiedExample ? 'info' : 'default'}
                                            sx={{
                                                position: 'absolute',
                                                top: 8,
                                                right: 8,
                                                bgcolor: 'background.paper',
                                                '&:hover': {
                                                    bgcolor: 'background.paper',
                                                },
                                            }}
                                        >
                                            <ContentCopyRounded
                                                fontSize="small"
                                                color={copiedExample ? 'info' : 'inherit'}
                                            />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </CardContent>
                        </Card>
                    </Stack>
                </>
            )}

            {apiKey && (
                <>
                    <Divider />
                    <Stack spacing={2}>
                        <Typography variant="subtitle2">{t('onboarding.dev.next-steps-title')}</Typography>
                        <Card
                            component={Link}
                            href={API_DOCS_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                textDecoration: 'none',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: (theme) => theme.shadows[4],
                                },
                            }}
                        >
                            <CardContent>
                                <Stack direction="row" alignItems="center" gap={2}>
                                    <Box
                                        sx={{
                                            p: 1.5,
                                            borderRadius: 2,
                                            bgcolor: 'primary.main',
                                            color: 'primary.contrastText',
                                            display: 'flex',
                                        }}
                                    >
                                        <CodeRounded />
                                    </Box>
                                    <Stack flex={1}>
                                        <Typography variant="subtitle2" fontWeight={600}>
                                            {t('onboarding.dev.api-docs')}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {t('onboarding.dev.api-docs-desc')}
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Stack>
                </>
            )}
        </Stack>
    );
};
